import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { products } from '@/mocks/products';

/** Product selections only. Customer details are never stored here or in browser storage. */
export type PickupBagItem = { productSlug: string; quantity: number };
export type Product = (typeof products)[number];
export type BagLine = PickupBagItem & { product: Product };

export const MAX_QTY = 24;
const KEY = 'fold-pickup-bag-v1';

const clampQty = (n: number) => Math.min(Math.max(Math.round(Number(n)) || 1, 1), MAX_QTY);
const findProduct = (slug: string) => products.find((p) => p.slug === slug);

/** Drops unknown slugs, merges duplicates, clamps quantities. */
export function sanitize(raw: unknown): PickupBagItem[] {
  if (!Array.isArray(raw)) return [];
  const out: PickupBagItem[] = [];
  raw.forEach((r) => {
    const slug = typeof r?.productSlug === 'string' ? r.productSlug : '';
    if (!findProduct(slug)) return;
    const existing = out.find((i) => i.productSlug === slug);
    if (existing) existing.quantity = clampQty(existing.quantity + Number(r.quantity));
    else out.push({ productSlug: slug, quantity: clampQty(r.quantity) });
  });
  return out;
}

function load(): PickupBagItem[] {
  if (typeof window === 'undefined') return [];
  try { return sanitize(JSON.parse(window.sessionStorage.getItem(KEY) ?? '[]')); } catch { return []; }
}

type Ctx = {
  items: PickupBagItem[];
  lines: BagLine[];
  totalQuantity: number;
  subtotal: number;
  addItem: (slug: string, quantity: number) => void;
  updateQuantity: (slug: string, quantity: number) => void;
  removeItem: (slug: string) => void;
  clearBag: () => void;
  bagOpen: boolean;
  setBagOpen: (open: boolean) => void;
};

const PickupBagContext = createContext<Ctx | null>(null);

export function PickupBagProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<PickupBagItem[]>(load);
  const [bagOpen, setBagOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.sessionStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage unavailable: bag stays in memory */ }
  }, [items]);

  const addItem = useCallback((slug: string, quantity: number) => {
    if (!findProduct(slug)) return;
    setItems((prev) => {
      const hit = prev.find((i) => i.productSlug === slug);
      if (hit) return prev.map((i) => (i.productSlug === slug ? { ...i, quantity: clampQty(i.quantity + quantity) } : i));
      return [...prev, { productSlug: slug, quantity: clampQty(quantity) }];
    });
  }, []);
  const updateQuantity = useCallback((slug: string, quantity: number) => {
    setItems((prev) => prev.map((i) => (i.productSlug === slug ? { ...i, quantity: clampQty(quantity) } : i)));
  }, []);
  const removeItem = useCallback((slug: string) => setItems((prev) => prev.filter((i) => i.productSlug !== slug)), []);
  const clearBag = useCallback(() => setItems([]), []);

  const value = useMemo<Ctx>(() => {
    const lines = items.flatMap((i) => {
      const product = findProduct(i.productSlug);
      return product ? [{ ...i, product }] : [];
    });
    return {
      items,
      lines,
      totalQuantity: lines.reduce((s, l) => s + l.quantity, 0),
      subtotal: lines.reduce((s, l) => s + l.quantity * l.product.price, 0),
      addItem, updateQuantity, removeItem, clearBag,
      bagOpen, setBagOpen,
    };
  }, [items, addItem, updateQuantity, removeItem, clearBag, bagOpen]);

  return <PickupBagContext.Provider value={value}>{children}</PickupBagContext.Provider>;
}

export function usePickupBag(): Ctx {
  const ctx = useContext(PickupBagContext);
  if (!ctx) throw new Error('usePickupBag must be used inside PickupBagProvider');
  return ctx;
}